import { type PaymentMethodItem, PaymentMethodType } from '@app/_proto/Protos/payments';
import type { SponsorPlanItem } from '@app/_proto/Protos/sponsors';
import type { SubscriptionItem } from '@app/_proto/Protos/subscriptions';
import { posthogHandler } from '@app/analytics/handlers';
import { ChampionEvents } from '@app/domains/champion/analytics/posthog/events';
import { championCommonLoggingParamsParser } from '@app/domains/champion/analytics/posthog/handlers';
import ChampionPlan from '@app/domains/champion/components/ChampionPlan';
import type { ChampionPlanProps, TierActionType } from '@app/domains/champion/types';
import { reducePlanActionType } from '@app/domains/champion/utils/reducePlanActionType';
import { checkVipFreeNovelSelectable } from '@app/domains/chapter/utils';
import useUnlocks from '@app/domains/common/hooks/useUnlocks';
import { ROUTE_PATH_MAP } from '@app/domains/common/utils/path';
import useVipFreeNovels from '@app/domains/novel/hooks/useVipFreeNovels';
import { PAYMENT_METHOD_TYPE_READABLE } from '@app/domains/subscription/constants';
import SubscribeDialog from '@app/domains/subscription/containers/SubscribeDialog';
import { useAuth } from '@app/libs/auth';
import { batch, decimalToNumber } from '@app/libs/utils';
import { Close } from '@mui/icons-material';
import { Button, Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { type ReactNode, type Ref, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type Props = {
    on?: string;
    plan: SponsorPlanItem;
    currentSubscription: SubscriptionItem | null;
    descLines?: number | undefined;
    showTierInfo?: boolean | undefined;
    onSelectPlan?: (plan: SponsorPlanItem) => void;
    onClickSubscribe?: (plan: SponsorPlanItem) => void;
    onSubscribePlan?: (subscription?: SubscriptionItem, paymentMethod?: PaymentMethodItem) => void;
    handleProcessingStateChange?: (processingState: boolean) => void;
    renderChampionPlan?: (props: ChampionPlanProps & { ref: Ref<HTMLDivElement> }) => ReactNode;
};

export default function ChampionPlanWithDialog({
    on,
    plan,
    currentSubscription,
    descLines,
    showTierInfo,
    onSelectPlan,
    onClickSubscribe,
    onSubscribePlan,
    handleProcessingStateChange,
    renderChampionPlan,
}: Props) {
    const { user } = useAuth();
    const [dialogOpened, setDialogOpened] = useState(false);
    const [cancelDialogOpened, setCancelDialogOpened] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

    const { data: unlockedNovels, isLoading: isUnlockedNovelsLoading } = useUnlocks({
        user: user ?? undefined,
        staleTime: 1000 * 60 * 60 * 24,
    });
    const { data: vipFreeNovels, isLoading: isVipFreeNovelsLoading } = useVipFreeNovels({ user: user ?? undefined });

    const navigate = useNavigate();

    const planRef = useRef<HTMLDivElement>(null);

    const handleTierAction = (action: TierActionType) => {
        if (action === 'unsubscribe') {
            setCancelDialogOpened(true);
        } else {
            purchaseOrEncourageVIP();
        }
    };

    const handleChangePaymentMethod = (e: React.MouseEvent<Element, MouseEvent>) => {
        e.preventDefault();
        openSubscribeDialog();
    };

    const openSubscribeDialog = useCallback(() => {
        onClickSubscribe?.(plan);
        setDialogOpened(true);
    }, [onClickSubscribe, plan]);

    const purchaseOrEncourageVIP = async () => {
        if (plan.novelInfo && user) {
            const shouldEncourageVipFreeNovelSelect = checkVipFreeNovelSelectable({
                user: user,
                novel: plan.novelInfo,
                vipFreeNovels,
                unlockedNovels,
            });

            if (shouldEncourageVipFreeNovelSelect) {
                setConfirmDialogOpen(true);
                return;
            }
        }
        openSubscribeDialog();
    };

    const handleConfirmClick = useCallback(() => {
        batch(() => {
            closeConfirmDialog();
            navigate(ROUTE_PATH_MAP.VIP_SUBSCRIPTION);
        });
    }, [navigate]);

    const handleSubscribeAnywaysClick = useCallback(() => {
        batch(() => {
            closeConfirmDialog();
            openSubscribeDialog();
        });
    }, [openSubscribeDialog]);

    const closeConfirmDialog = useCallback(() => {
        setConfirmDialogOpen(false);
    }, []);

    const shouldRenderSubscribeDialog = dialogOpened || cancelDialogOpened;
    const ChampionPlanComponent = renderChampionPlan || ChampionPlan;

    return (
        <>
            <ChampionPlanComponent
                actionType={reducePlanActionType(currentSubscription, plan)}
                disabled={!plan.enabled}
                name={plan.name}
                htmlDesc={plan?.description?.value}
                advChapterCount={plan.advanceChapterCount}
                price={decimalToNumber(plan.price)}
                discountInfo={plan.discountInfo}
                descLines={descLines}
                showTierInfo={showTierInfo}
                onAction={handleTierAction}
                onChangePaymentMethod={handleChangePaymentMethod}
                data-posthog-params={JSON.stringify({
                    ...championCommonLoggingParamsParser(plan),
                    actionType: reducePlanActionType(currentSubscription, plan),
                })}
                ref={planRef}
            />
            {shouldRenderSubscribeDialog && (
                <SubscribeDialog
                    opened={dialogOpened}
                    onClose={() => setDialogOpened(false)}
                    cancelOpened={cancelDialogOpened}
                    onCancelClose={() => setCancelDialogOpened(false)}
                    mode="confirm"
                    plan={plan}
                    subscription={currentSubscription}
                    onSubscribe={(champion, paymentMethod) => {
                        planRef.current?.setAttribute(
                            'data-posthog-params',
                            JSON.stringify({
                                on,
                                ...championCommonLoggingParamsParser(plan),
                                actionType: reducePlanActionType(currentSubscription, plan),
                                paymentMethod:
                                    PAYMENT_METHOD_TYPE_READABLE[paymentMethod?.type || PaymentMethodType.UnknownType],
                            })
                        );

                        posthogHandler(ChampionEvents.BuyChampionSubscribe, planRef.current as HTMLElement);
                        onSelectPlan?.(plan);
                        onSubscribePlan?.(champion, paymentMethod);
                    }}
                    onProcessingStateChange={handleProcessingStateChange}
                />
            )}
            {confirmDialogOpen && (
                <Dialog
                    open={confirmDialogOpen}
                    aria-labelledby="card-dialog-title"
                    fullWidth
                    PaperProps={{
                        className: 'dark:!bg-[#202020]',
                        style: {
                            maxWidth: '480px',
                            margin: '10px',
                            width: '100%',
                        },
                    }}
                >
                    <DialogTitle className="px-[20px] pt-[20px]">
                        <span className="text-[18px] font-bold leading-none">Confirm</span>
                        <IconButton aria-label="close" onClick={closeConfirmDialog} className="absolute right-0 top-0">
                            <Close />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent className="px-[20px] pb-[20px]">
                        <div className="space-y-[20px] text-[14px] text-[#666] dark:text-[#BDBDBD]">
                            <div>
                                Our system has detected your VIP subscription can grant access to this novel. Do you
                                want to obtain access to this novel through your VIP privileges? The selection resets at
                                the start of the month.
                            </div>
                        </div>
                        <div className="mt-[30px] flex flex-col sm:flex-row items-center justify-end gap-[10px]">
                            <Button
                                onClick={handleConfirmClick}
                                className="h-[35px]  w-full sm:w-auto !text-[14px] !font-semibold !leading-tight"
                            >
                                Select with VIP
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleSubscribeAnywaysClick}
                                className="h-[35px] w-full sm:w-auto !text-[14px] !font-semibold !leading-tight"
                            >
                                Subscribe anyways
                            </Button>
                            <Button
                                onClick={closeConfirmDialog}
                                variant="outlined"
                                color="secondary"
                                className="h-[35px] w-full sm:w-auto min-w-[94px] !text-[14px] !font-semibold !leading-tight"
                            >
                                Cancel
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
}
